import { Position, MarkerType } from '@xyflow/react';
import ELK from 'elkjs/lib/elk.bundled.js';
import { keyBy, stringify } from './miscUtils';
import { unitToString } from './unitUtils';
import {
  ANALYSIS_STATUS,
  BALANCER_NODE_NAMES,
  BLENDER_NODE_NAMES,
  CO2_EQUIVALENT,
  CO2_EQUIVALENT_EMISSION_TYPE,
  LIMITER_NODE_NAMES,
  NODE_TYPES,
  SYSTEM_NAMES_TO_TYPES,
  isArrowNodeName,
} from '@/consts';

// elk layouting options can be found here:
// https://www.eclipse.org/elk/reference/algorithms/org-eclipse-elk-layered.html
const layoutOptions = {
  'elk.algorithm': 'layered',
  'elk.direction': 'RIGHT',
  'elk.layered.layering.strategy': 'LONGEST_PATH_SOURCE',
  'elk.layered.spacing.edgeNodeBetweenLayers': '150',
  'elk.spacing.nodeNode': '80',
  'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
};
const elk = new ELK();

const getEstimatedNodeSize = node => {
  const portCount = Math.max(node.data.inputs.length, node.data.outputs.length);

  if ([NODE_TYPES.arrow, NODE_TYPES.limiter, NODE_TYPES.blender].includes(node.type)) {
    const height = Math.max(50, (portCount + 1) * 100 * 0.35);
    return { width: height * (Math.sqrt(3) / 2), height };
  }

  return {
    width: node.type === NODE_TYPES.balancer ? 384 : 448,
    height: 96 + portCount * 48,
  };
};

export const parseNode = node => {
  const { id, label, name, inputs, outputs, position, mirrored = false, type: initialType = 'pathwayNode', data } = node;

  let type = initialType;
  // Automatically switch the UI node type to 'arrowNode' for Splitter and Combiner modules
  // This renders them as compact triangles instead of the standard bulky boxes
  if (isArrowNodeName(name)) {
    type = NODE_TYPES.arrow;
  }
  // Automatically switch the UI node type to 'limiterNode' for StreamLimiter
  // This renders it as a compact triangle instead of the standard bulky box
  if (LIMITER_NODE_NAMES.includes(name)) {
    type = NODE_TYPES.limiter;
  }
  // Automatically switch the UI node type to 'balancerNode' for SupplyBalancer
  // This renders it as a compact box with recycle symbol instead of the standard bulky box
  if (BALANCER_NODE_NAMES.includes(name)) {
    type = NODE_TYPES.balancer;
  }
  // Automatically switch the UI node type to 'blenderNode' for CrudeOilBlender
  // This renders it as a compact box with oil droplet symbol instead of the standard bulky box
  if (BLENDER_NODE_NAMES.includes(name)) {
    type = NODE_TYPES.blender;
  }

  return {
    id,
    data: {
      id,
      label,
      name,
      inputs,
      outputs,
      mirrored,
      ...data,
    },
    position,
    type,
    sourcePosition: mirrored ? Position.Left : Position.Right,
    targetPosition: mirrored ? Position.Right : Position.Left,
  };
};

const handleSep = type => `~~~${type}~~~`;

export const buildHandle = (nodeId, type, name) => `${nodeId}${handleSep(type)}${name}`;

export const serializeHandle = (handle, type) => {
  const sep = handleSep(type);
  const [node, ...rest] = handle.split(sep);
  return [node, rest.join(sep)];
};

export const applyNodeInfo = (nodes, nodeInfo) => {
  return nodes.map(node => {
    let label = nodeInfo[node.id]?.name || node.label;
    let type = NODE_TYPES.pathway;
    const info = nodeInfo?.[node.id] ?? {};
    const { system_id: systemId, name, internal_pathway_id: internalPathwayId, internal_case_id: internalCaseId } = info;
    const data = {};

    if (systemId) {
      label = name || label;
      type = NODE_TYPES.system;
      data.system = {
        id: systemId,
        system_type: SYSTEM_NAMES_TO_TYPES[node.name],
        name: label,
      };
    } else if (isArrowNodeName(node.name)) {
      // Ensure existing/loaded Splitter/Combiner nodes also get the arrow type
      type = NODE_TYPES.arrow;
    } else if (LIMITER_NODE_NAMES.includes(node.name)) {
      // Ensure existing/loaded StreamLimiter nodes also get the limiter type
      type = NODE_TYPES.limiter;
    } else if (BALANCER_NODE_NAMES.includes(node.name)) {
      // Ensure existing/loaded SupplyBalancer nodes also get the balancer type
      type = NODE_TYPES.balancer;
    } else if (BLENDER_NODE_NAMES.includes(node.name)) {
      // Ensure existing/loaded CrudeOilBlender nodes also get the blender type
      type = NODE_TYPES.blender;
    }

    return { ...node, label, type, data: { ...data, internalPathwayId, internalCaseId }, internalPathwayId };
  });
};

/**
 * Serializes an edge into a format suitable for the server.
 * @param {Object} edge - The edge object to be serialized.
 * @returns {Object} - The serialized edge object in server format.
 */
export const serializeEdge = edge => {
  const { sourceHandle, targetHandle } = edge;
  const [output_node, output_name] = serializeHandle(sourceHandle, 'output');
  const [input_node, input_name] = serializeHandle(targetHandle, 'input');

  return {
    input_name,
    input_node,
    output_name,
    output_node,
  };
};

/**
 * Converts pathway nodes returned from the server into a format compatible with React Flow.
 * @param {Object[]} nodes - An array of pathway nodes to be parsed into React Flow format.
 * @returns {Object[]} - An array of nodes parsed into React Flow format.
 */
export const parseNodes = nodes => {
  return nodes.map(node => parseNode(node));
};

/**
 * Converts an connection received from the server into a format compatible with React Flow.
 * @param {Object} edge - The edge/connection object received from the server.
 * @returns {Object} - The parsed edge object in React Flow format.
 */
const parseEdge = edge => {
  const { sourceHandle, targetHandle, source, target, label, name, anchorable, unit, typeName, controlPoint } = edge;

  return {
    id: `${sourceHandle}-${targetHandle}`,
    label,
    source,
    target,
    type: 'pathwayEdge',
    markerEnd: {
      type: MarkerType.Arrow,
      width: 20,
      height: 20,
      color: '#000000',
    },
    style: {
      strokeWidth: 2,
      stroke: '#000000',
    },
    sourceHandle,
    targetHandle,
    selected: true,
    name,
    data: {
      name,
      anchorable,
      unit,
      typeName,
      controlPoint,
    },
  };
};

/**
 * Builds a stable key that uniquely identifies a connection by its endpoints.
 * Used to match server edge_info (control points) to React Flow edges.
 */
export const buildEdgeKey = ({ output_node, output_name, input_node, input_name }) =>
  `${output_node}~~~${output_name}~~~${input_node}~~~${input_name}`;

/**
 * Converts an array of node objects into a map where each key represents a node ID,
 * and the corresponding value is a node object augmented with inputs and outputs indexed by name.
 * @param {Object[]} nodes - An array of node objects to be transformed into a map.
 * @returns {Object<string, Object>} - A map where keys are node IDs and values are node objects
 * with inputs and outputs indexed by name.
 */
export const getNodesById = nodes => {
  return keyBy(nodes, 'id', node => ({
    ...node,
    inputsByName: keyBy(node.inputs, 'name'),
    outputsByName: keyBy(node.outputs, 'name'),
  }));
};

/**
 * Converts pathway connections returned from the server into React Flow edges.
 * @param {Object[]} connections - An array of pathway connections to be parsed into edges.
 * @param {Object<string, Object>} nodesById - A map where keys are node IDs and values are node objects
 * @param {Object[]} [edgeInfo] - Optional array of server edge_info entries holding control points.
 * @returns {Object[]} - An array of React Flow edges parsed from the pathway connections.
 */
export const parseEdges = (connections, nodesById, edgeInfo = []) => {
  const controlPointsByKey = (edgeInfo ?? []).reduce((acc, info) => {
    acc[buildEdgeKey(info)] = info.control_point;
    return acc;
  }, {});

  return connections.map(edge => {
    const { input_node: target, input_name: inputName, output_node: source, output_name: outputName } = edge;
    const sourceNode = nodesById[source];
    const targetNode = nodesById[target];
    const targetInput = targetNode.inputsByName[inputName];
    const output = sourceNode.outputsByName[outputName];
    const sourceHandle = buildHandle(source, 'output', outputName);
    const targetHandle = buildHandle(target, 'input', inputName);
    const outputType = output.type;
    const inputType = targetInput.type;
    let label = outputType.label;

    if (
      outputType.name === 'custom_feed' &&
      inputType.name === 'custom_feed' &&
      sourceNode.name === 'CustomFeedstock'
    ) {
      label = sourceNode.label;
    } else if (
      outputType.name === 'custom_feed' &&
      inputType.name === 'custom_feed' &&
      !sourceNode.name.match(/^Custom_/)
    ) {
      label = targetInput.label;
    } else if (outputType.name === 'custom_feed') {
      label = output.label;
    }

    const unit = outputType.unit;
    const name = outputName;
    const anchorable = output.anchorable;
    const controlPoint =
      controlPointsByKey[
        buildEdgeKey({ output_node: source, output_name: outputName, input_node: target, input_name: inputName })
      ];

    return parseEdge({
      typeName: outputType.name || inputType.name,
      sourceHandle,
      targetHandle,
      source,
      target,
      label,
      name,
      anchorable,
      unit,
      controlPoint,
    });
  });
};

// position each node
export const getLayoutedNodes = async (nodes, cachedNodes, edges) => {
  const graph = {
    id: 'root',
    layoutOptions,

    children: nodes.map(n => {
      const estimatedSize = getEstimatedNodeSize(n);
      const targetPorts = n.data.inputs.map(o => ({
        id: buildHandle(n.id, 'input', o.name),
      }));

      const sourcePorts = n.data.outputs.map(o => ({
        id: buildHandle(n.id, 'output', o.name),
      }));

      return {
        id: n.id,
        width: n.width ?? estimatedSize.width,
        height: n.height ?? estimatedSize.height,
        properties: {
          'org.eclipse.elk.portConstraints': 'FIXED_SIDE',
        },
        ports: [{ id: n.id }, ...targetPorts, ...sourcePorts],
      };
    }),
    edges: edges.map(e => ({
      id: e.id,
      sources: [e.sourceHandle || e.source],
      targets: [e.targetHandle || e.target],
    })),
  };

  const layoutedGraph = await elk.layout(graph);
  const layoutedNodes = nodes.map(node => {
    const layoutedNode = layoutedGraph.children?.find(lgNode => lgNode.id === node.id);

    return {
      ...node,
      position: {
        x: cachedNodes?.[node.id]?.x ?? layoutedNode?.x ?? 0,
        y: cachedNodes?.[node.id]?.y ?? layoutedNode?.y ?? 0,
      },
    };
  });

  return layoutedNodes;
};

export const getNodeOptions = nodes =>
  nodes.map(({ id, label }) => ({ label, value: id })).sort((a, b) => a.label.localeCompare(b.label));

export const getLevelizableIOOptions = levelizableIos => {
  return levelizableIos?.map(io => {
    return {
      value: stringify({ io_name: io.io_name, io_type: io.io_type }),
      label: io.label,
    };
  });
};

export const getIOOptions = node => {
  const outputResult =
    node?.outputs
      .filter(output => output.allocatable && output.levelizable !== false)
      .map(output => ({
        value: JSON.stringify({ io_name: output.name, io_type: 'output' }),
        label: output.label,
      })) || [];

  const inputResult =
    node?.inputs
      .filter(input => input.levelizable === true)
      .map(input => ({
        value: JSON.stringify({ io_name: input.name, io_type: 'input' }),
        label: input.label,
      })) || [];

  return [...outputResult, ...inputResult];
};

export const getEmissionTypes = nodes => {
  const emissionTypes = nodes.flatMap(getEmissionTypesPerNode);
  const emissionTypesByLabel = keyBy(emissionTypes, 'label');
  const result = Object.values(emissionTypesByLabel);

  result.unshift(CO2_EQUIVALENT_EMISSION_TYPE);

  return result;
};

const getEmissionTypesPerNode = node => {
  const emissionTypes = node?.emission_types?.filter(({ name }) => name !== CO2_EQUIVALENT) ?? [];
  const result = [];

  emissionTypes.forEach(({ name, label, unit }) => {
    result.push({ value: JSON.stringify({ co2_equivalent: false, emission_type: name, unit, label }), label });
    result.push({
      value: JSON.stringify({ co2_equivalent: true, emission_type: name, unit, label }),
      label: `${label} (in CO2e)`,
    });
  });

  return result;
};

export const getUnitsByIO = node => {
  const outputResult =
    (node?.outputs ?? [])
      .filter(output => output.allocatable && output.levelizable !== false)
      .map(output => ({
        io: JSON.stringify({ io_name: output.name, io_type: 'output' }),
        units: [output.type.unit, ...output.type.convertible_units],
      })) || [];

  const inputResult =
    (node?.inputs ?? [])
      .filter(input => input.levelizable === true)
      .map(input => ({
        io: JSON.stringify({ io_name: input.name, io_type: 'input' }),
        units: [input.type.unit, ...input.type.convertible_units],
      })) || [];

  return keyBy([...outputResult, ...inputResult], 'io');
};

export const getUnitOptions = units => {
  const uniqueUnits = new Set();

  return units
    ?.map(unit => ({
      label: unitToString(unit),
      value: stringify(unit),
    }))
    .filter(option => {
      if (uniqueUnits.has(option.value)) {
        return false;
      } else {
        uniqueUnits.add(option.value);
        return true;
      }
    });
};

export const isPathwaySucceeded = pathway =>
  pathway?.analysis_status?.tea === ANALYSIS_STATUS.succeeded &&
  pathway?.analysis_status?.lca === ANALYSIS_STATUS.succeeded &&
  !pathway?.analysis_stale;

export const getSucceededPathways = pathways => pathways.filter(pathway => isPathwaySucceeded(pathway));

/**
 * Extracts the unit name from a unit object
 * @param {Object|string} unit - Unit object or stringified unit
 * @returns {string|null} - The unit name (e.g., 'kg', 'kWh', 'ton') or null
 */
const getUnitName = unit => {
  if (!unit) return null;

  let unitObj;
  if (typeof unit === 'string') {
    try {
      unitObj = JSON.parse(unit);
    } catch {
      return null;
    }
  } else {
    unitObj = unit;
  }

  // Try label first, then numerator name
  if (unitObj.label) {
    return unitObj.label;
  }

  if (unitObj.numerator?.[0]?.name) {
    return unitObj.numerator[0].name;
  }

  return null;
};

/**
 * Default unit preference mapping - maps certain units to preferred alternatives
 * @type {Object<string, string>}
 */
export const DEFAULT_UNIT_PREFERENCES = {
  kg: 'ton',
  kWh: 'MWh',
};

export const getDefaultLevelization = (
  node,
  levelizableIos = null,
  selectedLevelizeBy = null,
  selectedLevelizeUnit = null,
  unitPreferences = null,
) => {
  const unitsByIO = getUnitsByIO(node);
  const levelizeByOptions = levelizableIos ? getLevelizableIOOptions(levelizableIos) : getIOOptions(node);
  const levelizableUnitsByIO = keyBy(
    levelizableIos?.map(io => ({
      io: stringify({ io_name: io.io_name, io_type: io.io_type }),
      units: io.units,
    })) || [],
    'io',
  );

  const option = levelizeByOptions.find(o => o.value === selectedLevelizeBy) ?? levelizeByOptions?.[0];
  const levelizeBy = option?.value;
  const levelizeByLabel = option?.label;

  const levelizeUnits = levelizableUnitsByIO?.[levelizeBy]?.units;
  const levelizeUnitOptions = getUnitOptions(levelizeUnits?.length ? levelizeUnits : unitsByIO?.[levelizeBy]?.units);

  let levelizeUnit;
  if (levelizeUnitOptions && selectedLevelizeUnit) {
    // Use selected unit if provided and available
    levelizeUnit =
      levelizeUnitOptions.find(o => o.value === selectedLevelizeUnit)?.value || levelizeUnitOptions?.[0]?.value;
  } else {
    // Check if default unit has a preference mapping
    const defaultUnit = levelizeUnitOptions?.[0]?.value;
    if (defaultUnit) {
      const defaultUnitName = getUnitName(defaultUnit);
      // Only use preferences if unitPreferences is provided (not null)
      const preferredUnitName = defaultUnitName && unitPreferences ? unitPreferences[defaultUnitName] : null;

      if (preferredUnitName) {
        // Try to find the preferred unit in available options
        const preferredOption = levelizeUnitOptions.find(option => {
          const optionUnitName = getUnitName(option.value);
          return optionUnitName === preferredUnitName;
        });

        if (preferredOption) {
          levelizeUnit = preferredOption.value;
        } else {
          levelizeUnit = defaultUnit;
        }
      } else {
        levelizeUnit = defaultUnit;
      }
    } else {
      levelizeUnit = null;
    }
  }

  const { io_name, io_type } = JSON.parse(levelizeBy || '{}');
  const unit = JSON.parse(levelizeUnit || '{}');

  return {
    levelizeBy,
    levelizeUnit,
    levelizeByLabel,
    levelizeByOptions,
    levelizeUnitOptions,
    levelization: {
      io_name,
      io_type,
      unit,
    },
  };
};

/**
 * Recursively checks if a param or any of its nested params are mc_eligible
 * @param {Object} param - The parameter object to check
 * @returns {boolean} - True if the param or any nested param is mc_eligible
 */
const hasMcEligibleParam = param => {
  if (param.mc_eligible) {
    return true;
  }

  if (param.params && Array.isArray(param.params)) {
    return param.params.some(nestedParam => hasMcEligibleParam(nestedParam));
  }

  return false;
};

/**
 * Recursively collects all mc_eligible params, including nested ones, in a flat array
 * @param {Array} params - Array of parameter objects
 * @param {Object} caseParams - Case parameters object
 * @returns {Object} - Object containing flat arrays of paramSchemas and caseParams
 */
const collectMcEligibleParams = (params, caseParams) => {
  const flatParams = [];
  const flatCaseParams = {};

  const traverse = (paramsList, currentCaseParams, parentPath = '', parentLabels = [], parentCategory = null) => {
    paramsList.forEach(param => {
      const fullName = parentPath ? `${parentPath}.params.${param.name}` : param.name;
      const labelPath = param.label ? [...parentLabels, param.label] : parentLabels;
      const fullLabel = labelPath.filter(label => label).join(' > ');
      const category = param?.category?.length ? param.category : parentCategory;

      if (param.mc_eligible && currentCaseParams[param.name]) {
        const paramWithFullName = {
          ...param,
          name: fullName,
          originalName: param.name,
          label: fullLabel || param.label,
          category,
        };

        flatParams.push(paramWithFullName);
        flatCaseParams[fullName] = currentCaseParams[param.name];
      }

      if (param.params && Array.isArray(param.params)) {
        const nestedCaseParams = currentCaseParams[param.name]?.params || {};
        traverse(param.params, nestedCaseParams, fullName, labelPath, category);
      }
    });
  };

  traverse(params, caseParams);

  return { flatParams, flatCaseParams };
};

export const getMonteCarloNodes = (nodes, caseNodes) => {
  const clonedNodes = structuredClone(nodes);

  const filteredNodes = clonedNodes.filter(node => node.params.some(param => hasMcEligibleParam(param)));
  const filteredCaseNodes = {};

  filteredNodes.forEach(node => {
    const nodeId = node.id;
    const caseNode = caseNodes[nodeId];

    if (caseNode) {
      const { flatParams, flatCaseParams } = collectMcEligibleParams(node.params, caseNode.params);
      node.params = flatParams;
      filteredCaseNodes[nodeId] = { params: flatCaseParams };
    }
  });

  return { filteredNodes, filteredCaseNodes };
};

/**
 * Reconstructs nested parameter structure from flattened params
 * Flattened params have fullName like "parent.params.child" which needs to be
 * converted back to nested structure: { parent: { params: { child: ... } } } before sending to the server.
 * @param {Object} flattenedNodes - Object with nodeId keys and flattened params
 * @param {boolean} uncertaintyActive - Value to set for uncertainty_active on all params
 * @returns {Object} - Nested structure expected by the server
 */
export const reconstructNestedParams = (flattenedNodes, uncertaintyActive) => {
  const nestedNodes = {};

  Object.keys(flattenedNodes).forEach(nodeId => {
    const flattenedParams = flattenedNodes[nodeId].params;
    const nestedParams = {};

    Object.keys(flattenedParams).forEach(fullName => {
      const param = {
        ...flattenedParams[fullName],
        uncertainty_active: { value: uncertaintyActive },
      };

      const pathParts = fullName.split('.params.');

      let current = nestedParams;
      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        if (!current[part]) {
          current[part] = { params: {} };
        }
        current = current[part].params;
      }

      const finalKey = pathParts[pathParts.length - 1];
      current[finalKey] = param;
    });

    nestedNodes[nodeId] = { params: nestedParams };
  });

  return nestedNodes;
};
